import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import { SearchWalletRequest, searchWallet } from "src/network/graphql/walletService";
import { Wallet } from "../wallets/walletSlice";
import { Transaction } from "../transactions/transactionSlice";
import { TransactionRequest, getAllTransaction } from "src/network/graphql/transactionService";
import { getUserWallets, searchUser } from "src/network/graphql/userService";
import { Users } from "../users/usersSlice";
import { organisationFilter, OrganisationFilterType } from "src/network/graphql/organisationService";
import { initialOrgList } from "../organisation/organisationSlice";
import { getGroupList, SearchGroupListRequest } from "src/network/graphql/groupService";
import { initialGroup } from "../group/groupSlice";
import { cardSearch } from "src/network/graphql/cardService";
import { formatFullName } from "src/utils";

const initialWallets: Wallet[] = [];

const initialTransaction: Transaction[] = [];

type walletTransaction = {
  id: number;
  current: number;
  previous: number;
  positive: boolean;
  diff: number;
};

const initialwalletTransaction: { [key: string]: walletTransaction } = {};

const initialUsers: Users[] = [];

type ChecklistSlot = "staff" | "sites" | "costCentres" | "cards" | "wallets" | "expenses";

// CPC dashboard KPI tiles: period-scoped transaction counts.
type DashStatSlot = "transactions" | "needReceipt" | "awaitingApproval" | "approved";

export type DashStatsPeriod = {
  min: string;
  max: string;
  organisationId?: string;
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    walletLoading: false,
    transLoading: false,
    depositLoading: false,
    walletTransLoading: false,
    userLoading: false,
    participantLoading: false,
    participantWalletLoading: false,
    topAccountLoading: false,
    error: null,
    topWallets: {
      wallets: initialWallets,
      total: 0
    },
    transList: {
      trans: initialTransaction,
      total: 0
    },
    depositList: {
      list: initialTransaction,
      total: 0
    },
    walletTransaction: initialwalletTransaction,
    users: {
      list: initialUsers,
      total: 0
    },
    participants: {
      list: initialUsers,
      total: 0
    },
    participantWallet: {
      wallets: initialWallets,
      total: 0
    },
    topAccounts: {
      list: initialOrgList,
      total: 0
    },
    groupLoading: false,
    group: {
      list: initialGroup,
      total: 0
    },
    onboardingChecklist: {
      staff: { total: 0, loading: true },
      sites: { total: 0, loading: true },
      costCentres: { total: 0, loading: true },
      cards: { total: 0, loading: true },
      wallets: { total: 0, loading: true },
      expenses: { total: 0, loading: true }
    },
    dashStats: {
      transactions: { total: 0, loading: true },
      needReceipt: { total: 0, loading: true },
      awaitingApproval: { total: 0, loading: true },
      approved: { total: 0, loading: true }
    }
  },
  reducers: {
    fetchWalletsStart: (state) => {
      state.walletLoading = true;
    },
    fetchWalletsSuccess: (state, action) => {
      state.walletLoading = false;
      state.topWallets.wallets = action.payload?.list ?? [];
      state.topWallets.total = action.payload?.total ?? 0;
    },
    fetchWalletsFail: (state, action) => {
      state.walletLoading = false;
      state.topWallets.wallets = [];
      state.topWallets.total = 0;
      state.error = action.payload;
    },
    fetchTransListStart: (state) => {
      state.transLoading = true;
    },
    fetchTransListSuccess: (state, action) => {
      state.transLoading = false;
      state.transList.trans = action.payload?.list ?? [];
      state.transList.total = action.payload?.total ?? 0;
    },
    fetchTransListFail: (state, action) => {
      state.transLoading = false;
      state.transList.trans = [];
      state.transList.total = 0;
      state.error = action.payload;
    },
    fetchDepositListStart: (state) => {
      state.depositLoading = true;
    },
    fetchDepositListSuccess: (state, action) => {
      state.depositLoading = false;
      state.depositList.list = action.payload?.list ?? [];
      state.depositList.total = action.payload?.total ?? 0;
    },
    fetchDepositListFail: (state, action) => {
      state.depositLoading = false;
      state.depositList.list = [];
      state.depositList.total = 0;
      state.error = action.payload;
    },
    fetchWalletTransactionsStart: (state) => {
      state.walletTransLoading = true;
    },
    fetchWalletTransactionsSuccess: (state, action) => {
      state.walletTransLoading = false;
      state.walletTransaction = action?.payload ?? [];
    },
    fetchWalletTransactionsFail: (state, action) => {
      state.walletTransLoading = false;
      state.walletTransaction = {};
      state.error = action.payload;
    },
    fetchUsersStart: (state) => {
      state.userLoading = true;
    },
    fetchUsersSuccess: (state, action) => {
      state.userLoading = false;
      state.users.list = action.payload?.list ?? [];
      state.users.total = action.payload?.total ?? 0;
    },
    fetchUsersFail: (state, action) => {
      state.userLoading = false;
      state.users.list = [];
      state.users.total = 0;
      state.error = action.payload;
    },
    fetchParticpantStart: (state) => {
      state.participantLoading = true;
    },
    fetchParticpantSuccess: (state, action) => {
      state.participantLoading = false;
      state.participants.list = action.payload?.list ?? [];
      state.participants.total = action.payload?.total ?? 0;
    },
    fetchParticpantFail: (state, action) => {
      state.participantLoading = false;
      state.participants.list = [];
      state.participants.total = 0;
      state.error = action.payload;
    },
    fetchParticipantWalletStart: (state) => {
      state.participantWalletLoading = true;
    },
    fetchParticipantWalletSuccess: (state, action) => {
      state.participantWalletLoading = false;
      state.participantWallet.wallets = action.payload?.list ?? [];
      state.participantWallet.total = action.payload?.total ?? 0;
    },
    fetchParticipantWalletFail: (state, action) => {
      state.participantWalletLoading = false;
      state.participantWallet.wallets = [];
      state.participantWallet.total = 0;
      state.error = action.payload;
    },
    resetParticipantWallet: (state, action) => {
      state.participantWallet.wallets = action.payload.list;
      state.participantWallet.total = action.payload?.total;
    },
    fetchTopAccountStart: (state) => {
      state.topAccountLoading = true;
    },
    fetchTopAccountSuccess: (state, action) => {
      state.topAccountLoading = false;
      state.topAccounts.list = action.payload?.list ?? [];
      state.topAccounts.total = action.payload?.total ?? 0;
    },
    fetchTopAccountFail: (state, action) => {
      state.topAccountLoading = false;
      state.topAccounts.list = [];
      state.topAccounts.total = 0;
      state.error = action.payload;
    },
    fetchGroupStart: (state) => {
      state.groupLoading = true;
    },
    fetchGroupSuccess: (state, action) => {
      state.groupLoading = false;
      state.group.list = action.payload?.list ?? [];
      state.group.total = action.payload?.total ?? 0;
    },
    fetchGroupFail: (state, action) => {
      state.groupLoading = false;
      state.group.list = [];
      state.group.total = 0;
      state.error = action.payload;
    },
    setChecklistSlotStart: (state, action) => {
      const slot = action.payload as ChecklistSlot;

      state.onboardingChecklist[slot].loading = true;
    },
    setChecklistSlotSuccess: (state, action) => {
      const { slot, total } = action.payload as { slot: ChecklistSlot; total: number };

      state.onboardingChecklist[slot].loading = false;
      state.onboardingChecklist[slot].total = total;
    },
    setChecklistSlotFail: (state, action) => {
      const { slot, error } = action.payload as { slot: ChecklistSlot; error?: unknown };

      state.onboardingChecklist[slot].loading = false;
      if (error !== undefined) state.error = error as typeof state.error;
    },
    setDashStatStart: (state, action) => {
      const slot = action.payload as DashStatSlot;

      state.dashStats[slot].loading = true;
    },
    setDashStatSuccess: (state, action) => {
      const { slot, total } = action.payload as { slot: DashStatSlot; total: number };

      state.dashStats[slot].loading = false;
      state.dashStats[slot].total = total;
    },
    setDashStatFail: (state, action) => {
      const { slot, error } = action.payload as { slot: DashStatSlot; error?: unknown };

      state.dashStats[slot].loading = false;
      if (error !== undefined) state.error = error as typeof state.error;
    }
  }
});

const {
  fetchWalletsStart,
  fetchWalletsSuccess,
  fetchWalletsFail,
  fetchTransListStart,
  fetchTransListSuccess,
  fetchTransListFail,
  fetchDepositListStart,
  fetchDepositListSuccess,
  fetchDepositListFail,
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFail,
  fetchParticpantStart,
  fetchParticpantSuccess,
  fetchParticpantFail,
  fetchParticipantWalletStart,
  fetchParticipantWalletSuccess,
  fetchParticipantWalletFail,
  fetchTopAccountStart,
  fetchTopAccountSuccess,
  fetchTopAccountFail,
  fetchGroupFail,
  fetchGroupStart,
  fetchGroupSuccess,
  setChecklistSlotStart,
  setChecklistSlotSuccess,
  setChecklistSlotFail,
  setDashStatStart,
  setDashStatSuccess,
  setDashStatFail
} = dashboardSlice.actions;

export const { resetParticipantWallet } = dashboardSlice.actions;

export const fetchTopWalletAction = (data: SearchWalletRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWalletsStart());
    try {
      const response = await searchWallet(data);

      const structuredResponse = response.data.search_wallet.items.map((wallet: any) => ({
        id: wallet.wallet_id,
        displayId: wallet.display_id,
        description: wallet.description,
        balance: wallet.balance,
        status: wallet.wallet_status,
        participant: { role: "PARTICIPANT", fullName: wallet.name }
      }));

      dispatch(fetchWalletsSuccess({ list: structuredResponse, total: structuredResponse.length }));
    } catch (error) {
      dispatch(fetchWalletsFail(error));
    }
  };
};

export const fetchTransListAction = (data: TransactionRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchTransListStart());
    try {
      const response = await getAllTransaction(data);
      const structuredResponse = response.data.search_activity.activity_listing.map((data: any) => ({
        id: data.activity_id,
        name: data.activity_name,
        endDate: data.end_timestamp,
        startDate: data.start_timestamp,
        ownerId: data.owner_id,
        activityStatus: data.activity_status,
        approval_number: data.approval_number,
        data: JSON.parse(data?.data)
      }));

      dispatch(
        fetchTransListSuccess({
          list: structuredResponse,
          total: response.data.search_activity.total_size
        })
      );
      return response;
    } catch (err) {
      dispatch(fetchTransListFail(err));
    }
  };
};

export const fetchDepositListAction = (data: TransactionRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchDepositListStart());
    try {
      const response = await getAllTransaction(data);
      const structuredResponse = response.data.search_activity.activity_listing.map((data: any) => ({
        id: data.activity_id,
        name: data.activity_name,
        endDate: data.end_timestamp,
        startDate: data.start_timestamp,
        ownerId: data.owner_id,
        activityStatus: data.activity_status,
        approval_number: data.approval_number,
        data: JSON.parse(data?.data)
      }));

      dispatch(
        fetchDepositListSuccess({
          list: structuredResponse,
          total: response.data.search_activity.total_size
        })
      );
      return response;
    } catch (err) {
      dispatch(fetchDepositListFail(err));
    }
  };
};

export const fetchWalletTransactionAction = (data: any) => {
  return data;
};

export const fetchParticpantAction = (
  data: {
    isActive?: boolean | null;
    email?: string;
    fullName?: string;
    organisationId?: string | null;
    limit?: number;
    from?: number;
    role?: string;
  },
  cb?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchParticpantStart());
    try {
      const response = await searchUser(data);

      const structuredResponse = response.data.search_user.user_listing.map((user: any) => ({
        id: user?.display_id,
        firstName: user?.attributes?.first_name,
        lastName: user?.attributes?.last_name,
        email: user?.email,
        role: user?.current_organisation_role,
        status: user?.is_active,
        userId: user?.user_id,
        createdAt: user?.created_at
      }));

      dispatch(
        fetchParticpantSuccess({
          list: structuredResponse,
          total: response.data.search_user.total_size
        })
      );
      if (cb) cb();
    } catch (error) {
      dispatch(fetchParticpantFail(error));
    }
  };
};

export const fetchParticipantWalletAction = (userId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchParticipantWalletStart());
    try {
      const response = await getUserWallets(userId);

      const structuredResponse = response.data.get_user_wallets.map((wallet: any) => ({
        id: wallet.wallet_id,
        displayId: wallet.display_id,
        description: wallet.description,
        balance: wallet.balance,
        // status: wallet.wallet_status,
        walletType: wallet.wallet_type,
        name: wallet.name,
        participant: {
          role: "PARTICIPANT",
          email: wallet?.user?.email,
          fullName: formatFullName(wallet?.user?.attributes?.first_name, wallet?.user?.attributes?.last_name)
        }
      }));

      dispatch(
        fetchParticipantWalletSuccess({
          list: structuredResponse,
          total: structuredResponse.length
        })
      );
    } catch (error) {
      dispatch(fetchParticipantWalletFail(error));
    }
  };
};

export const fetchUserListAction = (
  data: {
    isActive?: boolean | null;
    email?: string;
    fullName?: string;
    organisationId?: string | null;
    limit?: number;
    from?: number;
    role?: string;
  },
  cb?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUsersStart());
    try {
      const response = await searchUser({
        user_filter: {
          is_active: data.isActive,
          email: data.email,
          full_name: data.fullName,
          organisation_id: data.organisationId,
          role: data.role
        },
        size: data.limit,
        from: data.from
      });

      const structuredResponse = response.data.search_user.user_listing.map((user: any) => ({
        id: user?.display_id,
        firstName: user?.attributes?.first_name,
        lastName: user?.attributes?.last_name,
        email: user?.email,
        role: user?.current_organisation_role,
        status: user?.is_active,
        userId: user?.user_id,
        createdAt: user?.created_at
      }));

      dispatch(
        fetchUsersSuccess({
          list: structuredResponse,
          total: response.data.search_user.total_size
        })
      );
      if (cb) cb();
    } catch (error) {
      dispatch(fetchUsersFail(error));
    }
  };
};

export const fetchTopAccountAction = (data: OrganisationFilterType) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchTopAccountStart());
    try {
      const response = await organisationFilter(data);

      const structuredResponse = response.data.search_organisation.organisation_listing.map((org: any) => ({
        organisationId: org.organisation_id,
        id: org.display_id,
        active: org.is_active,
        type: org.type,
        referralCode: org?.referral_code,
        name: org.name,
        createdAt: org.created_at,
        referenceName: org.contact.name,
        phone: org.contact.phone
      }));

      dispatch(
        fetchTopAccountSuccess({
          list: structuredResponse,
          total: response.data.search_organisation.total_size
        })
      );
    } catch (error) {
      dispatch(fetchTopAccountFail(error));
    }
  };
};

export const fetchGroupListAction = (data: SearchGroupListRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupStart());
    try {
      const response = await getGroupList(data);

      const structuredResponse = response.data.search_group?.items;

      dispatch(
        fetchGroupSuccess({
          list: structuredResponse,
          total: response.data.search_group.total_size
        })
      );
    } catch (error) {
      dispatch(fetchGroupFail(error));
    }
  };
};

const runChecklistCount = async (dispatch: StoreDispatch, slot: ChecklistSlot, fetchTotal: () => Promise<number>) => {
  dispatch(setChecklistSlotStart(slot));
  try {
    const total = await fetchTotal();

    dispatch(setChecklistSlotSuccess({ slot, total }));
  } catch (error) {
    dispatch(setChecklistSlotFail({ slot, error }));
  }
};

export const fetchChecklistStaffCountAction = (staffRoles: string[]) => async (dispatch: StoreDispatch) =>
  runChecklistCount(dispatch, "staff", async () => {
    const response = await searchUser({
      user_filter: { roles: staffRoles },
      size: 1,
      from: 0
    });

    return response.data.search_user.total_size ?? 0;
  });

const groupCountThunk =
  (slot: "sites" | "costCentres", groupType: "SITE" | "COST_CENTRE") => () => async (dispatch: StoreDispatch) =>
    runChecklistCount(dispatch, slot, async () => {
      const response = await getGroupList({
        size: 1,
        from: 0,
        filter: {
          group_filter: {
            group_type: groupType
          }
        }
      });

      return response.data.search_group?.total_size ?? 0;
    });

export const fetchChecklistSiteCountAction = groupCountThunk("sites", "SITE");
export const fetchChecklistCostCentreCountAction = groupCountThunk("costCentres", "COST_CENTRE");

export const fetchChecklistCardCountAction = () => async (dispatch: StoreDispatch) =>
  runChecklistCount(dispatch, "cards", async () => {
    const response = await cardSearch({ size: 1, from: 0, card_filter: {} });

    return response.data.search_card?.total_size ?? 0;
  });

export const fetchChecklistWalletCountAction = () => async (dispatch: StoreDispatch) =>
  runChecklistCount(dispatch, "wallets", async () => {
    const response = await searchWallet({ size: 1, from: 0, walletFilter: {} });

    return response.data.search_wallet?.total_size ?? 0;
  });

export const fetchChecklistExpenseCountAction = (organisationId?: string) => async (dispatch: StoreDispatch) =>
  runChecklistCount(dispatch, "expenses", async () => {
    const response = await getAllTransaction({
      size: 1,
      from: 0,
      filter: { organisation_id: organisationId }
    });

    return response.data.search_activity?.total_size ?? 0;
  });

const runDashStatCount = async (
  dispatch: StoreDispatch,
  slot: "transactions" | "needReceipt" | "awaitingApproval" | "approved",
  filter: TransactionRequest["filter"]
) => {
  dispatch(setDashStatStart(slot));
  try {
    const response = await getAllTransaction({ size: 1, from: 0, filter });

    dispatch(setDashStatSuccess({ slot, total: response.data.search_activity?.total_size ?? 0 }));
  } catch (error) {
    dispatch(setDashStatFail({ slot, error }));
  }
};

/**
 * CPC dashboard KPI counts for the selected period (mirrors the mobile home
 * dashboard: transactions, need receipt, awaiting approval, approved).
 * Status tiles count card transactions, matching the transactions screen's
 * account-status filters so a tile's click-through always agrees with it.
 *
 * "Awaiting approval" is the exception: it uses `activity_needs_approval`, the
 * same reviewer-scoped filter as Transactions -> Needs attention -> Approval
 * Required (see buildActionRequiredFilter). search-service resolves that to the
 * caller's reviewer wallets, so the tile counts what *this* user has to approve.
 *
 * It previously filtered on `accounting_status: SUBMITTED` scoped only by
 * organisation_id — an org-wide total with no approver scoping at all. That made
 * the tile disagree with the Needs-attention queue it links to, and approvers
 * reported not seeing the transactions they are responsible for.
 */
export const fetchDashboardStatsAction = (period: DashStatsPeriod) => async (dispatch: StoreDispatch) => {
  const base = {
    organisation_id: period.organisationId,
    created_at: { min: period.min, max: period.max }
  };
  const cardBase = { ...base, activityName: "NOVATTI_CARD_TRANSACTION" };

  await Promise.all([
    runDashStatCount(dispatch, "transactions", base),
    runDashStatCount(dispatch, "needReceipt", {
      ...cardBase,
      accounting_status: "DRAFT",
      receipt_available: false
    }),
    runDashStatCount(dispatch, "awaitingApproval", { ...cardBase, activity_needs_approval: true }),
    runDashStatCount(dispatch, "approved", { ...cardBase, accounting_status: "APPROVED" })
  ]);
};

export default dashboardSlice.reducer;
